import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Loader from '../../../../presentationals/Loader/Loader.component';
import DateFilterSelect from '../../../../CustomDatePicker/DateFilterSelectold';
import { useSelector, useDispatch } from 'react-redux';
import { FILTER_ACTION } from '../../../../../reducers/CommonFilters.reducer';
import {
  getPostApprovalSummary,
  getPostApprovalFunnel,
  getPostApprovalRejectionReasons,
  getPostApprovalRejectionDetails,
  getPostApprovalRejectionsByStage,
  getPostApprovalRejectionsByDepartment,
  getPostApprovalExpiresByStage,
  getPostApprovalExpiresByDepartment
} from '../../AdminActionCreator.component';

import SummaryCards from './SummaryCards';
import FunnelSection from './FunnelSection';
import ChartsSection, { RejectionReasonsPanel } from './ChartsSection';
import RejectionDetailsTable from './RejectionDetailsTable';

export default function Main({pageViewHandler}) {
  const dispatch = useDispatch();
  const { fromDate, toDate } = useSelector(state => state.FilterReduce.commonFilters);

  // Local mirror of the Redux filter — same pattern as Marketing Dashboard
  const [commonFilter, setCommonfilter] = useState({ fromDate, toDate });

  // Keep local mirror in sync when Redux changes from outside (another tab/component)
  useEffect(() => {
    setCommonfilter({ fromDate, toDate });
  }, [fromDate, toDate]);

  // Dispatch to Redux AND update local state
  const handleFilter = (type, value) => {
    dispatch({ type: FILTER_ACTION, payload: { [type]: value } });
    setCommonfilter(prev => ({ ...prev, [type]: value }));
  };

  // Called by DateFilterSelect onChange — same logic as Dashboard.jsx
  const handleDateFilterPopup = (type, val) => {
    const formatted = moment(val).format('YYYY-MM-DD');

    if (type === 'from') {
      if (commonFilter.fromDate !== formatted) {
        handleFilter('fromDate', formatted);
      }
    } else {
      if (commonFilter.toDate !== formatted) {
        handleFilter('toDate', formatted);
      }
    }
  };

  const [isFilterCleared, setIsFilterCleared] = useState(false);

  // API data states
  const [summaryData, setSummaryData] = useState({});
  const [funnelData, setFunnelData] = useState([]);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [rejectionsByStage, setRejectionsByStage] = useState([]);
  const [rejectionsByDepartment, setRejectionsByDepartment] = useState([]);
  const [expiresByStage, setExpiresByStage] = useState([]);
  const [expiresByDepartment, setExpiresByDepartment] = useState([]);
  const [rejectionDetails, setRejectionDetails] = useState({ total: 0, data: [] });
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [loader, setLoader] = useState(false);

  // ── Fetch all dashboard stats (7 parallel callbacks)
  const fetchDashboardStats = (filter) => {
    const startStr = moment(filter.fromDate).format('DD-MM-YYYY');
    const endStr = moment(filter.toDate).format('DD-MM-YYYY');

    setLoader(true);
    let pending = 7;
    const checkDone = () => { pending--; if (pending == 0) setLoader(false); };

    getPostApprovalSummary(startStr, endStr, (res) => {
      if (res?.status === 200 && res.data) setSummaryData(res.data);
      checkDone();
    });
    getPostApprovalFunnel(startStr, endStr, (res) => {
      if (res?.status === 200 && res.data) setFunnelData(res.data);
      checkDone();
    });
    getPostApprovalRejectionReasons(startStr, endStr, (res) => {
      if (res?.status === 200 && res.data) setRejectionReasons(res.data);
      checkDone();
    });
    getPostApprovalRejectionsByStage(startStr, endStr, (res) => {
      if (res?.status === 200 && res.data) setRejectionsByStage(res.data);
      checkDone();
    });
    getPostApprovalRejectionsByDepartment(startStr, endStr, (res) => {
      if (res?.status === 200 && res.data) setRejectionsByDepartment(res.data);
      checkDone();
    });
    getPostApprovalExpiresByStage(startStr, endStr, (res) => {
      if (res?.status === 200 && res.data) setExpiresByStage(res.data);
      checkDone();
    });
    getPostApprovalExpiresByDepartment(startStr, endStr, (res) => {
      if (res?.status === 200 && res.data) setExpiresByDepartment(res.data);
      checkDone();
    });
  };

  // ── Fetch paginated rejection details only
  const fetchDetails = (filter, page) => {
    const startStr = moment(filter.fromDate).format('DD-MM-YYYY');
    const endStr = moment(filter.toDate).format('DD-MM-YYYY');
    getPostApprovalRejectionDetails(startStr, endStr, page, pageSize, (res) => {
      if (res?.status === 200 && res.data) setRejectionDetails(res.data);
    });
  };

  // ── Re-fetch everything when commonFilter changes (date changes from anywhere)
  useEffect(() => {
    setCurrentPage(0);
    fetchDashboardStats(commonFilter);
    fetchDetails(commonFilter, 0);
  }, [commonFilter]);

  // ── Re-fetch only details table when page changes (date unchanged)
  useEffect(() => {
    fetchDetails(commonFilter, currentPage);
  }, [currentPage]);

  const displayStart = moment(commonFilter.fromDate).format('DD MMM YYYY');
  const displayEnd = moment(commonFilter.toDate).format('DD MMM YYYY');

  return (
    <div style={{ padding: '24px', background: '#f4f6fb', minHeight: '100vh', position: 'relative' }}>
      {loader && <Loader />}

      {/* ── PAGE TITLE + CONTROLS ────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Back button */}
          <button
            onClick={(e) => pageViewHandler(e,"landingPage")}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '34px',
              height: '34px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: '#ffffff',
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              flexShrink: 0,
              transition: 'all 0.15s'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
            title="Go back"
          >
            <svg style={{ width: 'auto' }} height="16" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a', margin: 0, letterSpacing: '-0.3px' }}>
              Post Approval Funnel Dashboard
            </h1>
            <p style={{ fontSize: '13px', color: '#64748b', marginTop: '4px', fontWeight: '400', margin: '4px 0 0' }}>
              Track loan applications journey from Approved to Disbursement
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>

      

          {/* DateFilterSelect — triggers handleDateFilterPopup on change */}
          <DateFilterSelect
            value={[
              commonFilter.fromDate ? new Date(commonFilter.fromDate) : new Date(),
              commonFilter.toDate ? new Date(commonFilter.toDate) : new Date()
            ]}
            onChange={(range) => {
              if (range && range[0]) handleDateFilterPopup('from', range[0]);
              if (range && range[1]) handleDateFilterPopup('to', range[1]);
            }}
            isFilterCleared={isFilterCleared}
            customStyle={{
              background: '#ffffff',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              border: '1px solid #e2e8f0',
            }}
          />

         

          {/* Export button */}
          <button
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '7px 14px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: '#ffffff',
              fontSize: '13px',
              fontWeight: '600',
              color: '#334155',
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              transition: 'all 0.15s'
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#a5b4fc'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
          >
            <svg style={{ width: 'auto', color: '#64748b' }} height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export
          </button>
        </div>
      </div>

      {/* ── ROW 0: KPI SUMMARY CARDS ─────────────────────────────────────── */}
      <SummaryCards summaryData={summaryData} />

      {/* ── ROW 1: Funnel (58%) + Rejection Reasons (42%) — same row ── */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', alignItems: 'stretch' }}>
        <div style={{ flex: '0 0 58.33%', maxWidth: '58.33%', minWidth: 0 }}>
          <FunnelSection funnelData={funnelData} />
        </div>
        <div style={{ flex: '0 0 calc(41.67% - 16px)', maxWidth: 'calc(41.67% - 16px)', minWidth: 0 }}>
          <RejectionReasonsPanel
            rejectionReasons={rejectionReasons}
            totalRejected={summaryData.rejectedLeads}
          />
        </div>
      </div>

      {/* ── ROW 2: 3-COLUMN CHARTS ───────────────────────────────────────── */}
      <ChartsSection
        rejectionsByStage={rejectionsByStage}
        rejectionsByDepartment={rejectionsByDepartment}
        expiresByStage={expiresByStage}
        expiresByDepartment={expiresByDepartment}
        summaryData={summaryData}
      />

      {/* ── ROW 3: REJECTION DETAILS TABLE ───────────────────────────────── */}
      <RejectionDetailsTable
        rejectionDetails={rejectionDetails}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={() => {}}
      />
    </div>
  );
}
